// Copy / cut / paste for slide objects.
//
// The clipboard has to outlive the slide it was filled on — the whole point is
// cutting from one slide and pasting on another — so it lives outside React
// state. An in-memory copy answers instantly; localStorage backs it up so the
// clipboard also survives a reload, matching how the multi-column card
// clipboard already behaves.

const STORAGE_KEY = 'boppps.slideBlockClipboard';

let memoryClipboard = null;

const clone = (value) => {
    try {
        return typeof structuredClone === 'function'
            ? structuredClone(value)
            : JSON.parse(JSON.stringify(value));
    } catch {
        // A block holding something unserializable (a File, say) should not
        // take the whole clipboard down with it.
        try {
            return JSON.parse(JSON.stringify(value));
        } catch {
            return null;
        }
    }
};

/** Put blocks on the clipboard. Passing nothing clears it. */
export const writeBlockClipboard = (blocks) => {
    const list = Array.isArray(blocks) ? blocks.filter(Boolean) : [];
    if (!list.length) {
        memoryClipboard = null;
        try {
            if (typeof window !== 'undefined') window.localStorage.removeItem(STORAGE_KEY);
        } catch { /* storage unavailable — the in-memory copy still works */ }
        return 0;
    }
    const copied = clone(list) || [];
    memoryClipboard = copied;
    try {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(copied));
        }
    } catch { /* quota or private mode — in-memory is enough for this session */ }
    return copied.length;
};

/** Blocks currently on the clipboard, or an empty array. */
export const readBlockClipboard = () => {
    if (memoryClipboard) return clone(memoryClipboard) || [];
    try {
        if (typeof window === 'undefined') return [];
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const hasBlockClipboard = () => readBlockClipboard().length > 0;

/**
 * Turn clipboard blocks into ones that can be dropped onto a slide.
 *
 * Fresh ids stop a paste colliding with the block it was copied from, and any
 * group is re-formed under a new id so the copies group with each other rather
 * than joining the original set. Frames shift by `offset` so a paste onto the
 * same slide lands beside the original instead of exactly on top of it.
 *
 * @param {Array}    blocks     from readBlockClipboard()
 * @param {Function} makeId     id generator, e.g. () => uid('block')
 * @param {Object}   options    { offset, normalizeFrame }
 */
export const prepareBlocksForPaste = (blocks, makeId, { offset = 3, normalizeFrame } = {}) => {
    const groupIdMap = new Map();

    return (blocks || []).filter(Boolean).map((block, index) => {
        const next = { ...block, id: makeId(block, index) };

        if (block.groupId) {
            if (!groupIdMap.has(block.groupId)) groupIdMap.set(block.groupId, makeId(block, index));
            next.groupId = groupIdMap.get(block.groupId);
        }

        if (block.frame) {
            const shifted = {
                ...block.frame,
                x: (Number(block.frame.x) || 0) + offset,
                y: (Number(block.frame.y) || 0) + offset
            };
            next.frame = normalizeFrame ? normalizeFrame(shifted) : shifted;
        }
        return next;
    });
};
